import {
  Box,
  Skeleton,
  Table,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import LinearProgress, {
  linearProgressClasses,
} from "@mui/material/LinearProgress";
import Typography from "@mui/material/Typography";
import * as React from "react";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";

import {
  fetchStoreMonthlyBreakdown,
  storeAnalyticsSelector,
} from "../../../../../redux/slices/store_analytics";
import { months } from "../../constants";

import { useGoalsStores } from "./useGoalsStores";

const TableRowStyled = styled(TableRow)`
  &.Mui-selected {
    background-color: #d3e9ff !important;
  }
`;

const TableCellStyled = styled(TableCell)`
  font-family: Inter;
  font-weight: 600;
  font-size: 14px;
  color: #000000;
  padding: 21px 16px;
`;

const InnerTableCellStyled = styled(TableCell)`
  font-family: Inter;
  font-weight: 400;
  font-size: 14px;
  color: #000000;
  width: 5.5%;
  // padding: 0px 30px;
  // max-width: 50px;
`;

const GoalText = styled.p`
  padding: 0;
  margin: 0;
`;

const InputStyled = styled.input`
  width: 60px;
`;

const PredictedChart = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`;

const BorderLinearProgress = styled(LinearProgress)(({ goalColor }) => ({
  height: 10,
  width: "150px",
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: "#eeeeee",
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: goalColor,
  },
}));

const isPastCalendarMonth = (year, monthName) => {
  if (!monthName || monthName === "Total" || monthName === "all") return false;
  const now = new Date();
  const y = Number(year);
  const monthIndex = months.indexOf(monthName);
  if (monthIndex < 0) return false;
  return (
    y < now.getFullYear() ||
    (y === now.getFullYear() && monthIndex < now.getMonth())
  );
};

const storeHasMonthlyGoalRow = (item) => {
  if (item.has_monthly_goal === true) return true;
  if (item.has_monthly_goal === false) return false;
  // Staging API before has_monthly_goal: a missing monthly_goals row is
  // returned as a zeroed placeholder. Treat that as "not in portfolio yet".
  return (
    (Number(item.goal) || 0) !== 0 || (Number(item.profit_goal) || 0) !== 0
  );
};

const goalsForBreakdown = (goals, year, monthName) => {
  const list = Array.isArray(goals) ? goals : [];
  if (!isPastCalendarMonth(year, monthName)) return list;
  return list.filter(storeHasMonthlyGoalRow);
};

const GoalsTableRow = ({
  data,
  onClick,
  selected,
  editableRow,
  onChangeGoal,
  activeRowGoal,
  expendedRow,
}) => {
  const [lineExpended, setLineExpended] = useState(expendedRow);
  const dispatch = useDispatch();
  const goalsStores = useGoalsStores();

  const onExpand = (shouldFetch = false) => {
    setLineExpended(!lineExpended);
    if (shouldFetch && !storeMonthlyBreakdownLoading && !lineExpended) {
      dispatch(
        fetchStoreMonthlyBreakdown({
          month: data.month_,
          year: data.year,
          store: data.store,
          stores: goalsStores,
        })
      );
    }
  };

  const showTodayPrecentage = () => {
    return data.month_ && data.month === months[new Date().getMonth()];
  };

  const precentageGoalToday = () => {
    let monthDays = new Date(
      data.year,
      data.month_ ? data.month_ : new Date().getMonth() + 1,
      0
    ).getDate();
    let res =
      Math.round(
        ((monthDays - data.days_left - 0) / monthDays) * 100
      ).toString() + "%";
    return res;
  };

  const isYearlyTotal = () => {
    return data.month_ === 13 && data.month === "Total";
  };

  const yearlyPrecentageToday = () => {
    const isLeapYear =
      (data.year % 4 === 0 && data.year % 100 !== 0) || data.year % 400 === 0;
    const yearDays = isLeapYear ? 366 : 365;
    const daysPassed = yearDays - data.days_left;
    return Math.round((daysPassed / yearDays) * 100).toString() + "%";
  };

  const calcRelativeGoal = (ratio) => {
    let monthDays = new Date(
      data.year,
      data.month_ ? data.month_ : new Date().getMonth() + 1,
      0
    ).getDate();
    let daysPassed =
      data.month === new Date().toLocaleString("default", { month: "long" })
        ? monthDays - data.days_left - 0 //no need for compensation, server returns in us pacific time
        : monthDays - data.days_left;
    let PrecentageMonthPassed = daysPassed / monthDays;
    return ratio / PrecentageMonthPassed;
  };

  const calcYearlyRelativeGoal = (ratio) => {
    const isLeapYear =
      (data.year % 4 === 0 && data.year % 100 !== 0) || data.year % 400 === 0;
    const yearDays = isLeapYear ? 366 : 365;
    const daysPassed = yearDays - data.days_left;
    const percentageYearPassed = daysPassed / yearDays;
    return ratio / percentageYearPassed;
  };

  const progressColor = (light, ratio) => {
    const isYearlyTotal =
      (data.month_ === 13 && data.month === "Total") || data.month === "all";

    let goalValue;
    if (isYearlyTotal) {
      goalValue = Math.round(calcYearlyRelativeGoal(ratio));
    } else {
      goalValue = Math.round(calcRelativeGoal(ratio));
    }

    if (goalValue > 99) {
      return light ? "LightGreen" : "green";
    } else if (goalValue < 50) {
      return light ? "LightCoral" : "red";
    } else if (goalValue === 0) {
      return "lightblue";
    } else {
      return light ? "LightBlue" : "#6495ed";
    }
  };

  const {
    storeMonthlyBreakdownData,
    storeMonthlyBreakdownLoading,
    storeMonthlyBreakdownMonth,
  } = useSelector(storeAnalyticsSelector);

  const innerTable = data.month_ ? false : true;
  const breakdownGoals = goalsForBreakdown(
    storeMonthlyBreakdownData?.[data.month_]?.goals,
    data.year,
    data.month
  );
  const sumProfit = breakdownGoals.reduce(
    (sum, goal) => sum + goal.profit_to_date + goal.actual_extra_profit,
    0
  );
  const sumProfitGoal = breakdownGoals.reduce(
    (sum, goal) => sum + goal.profit_goal,
    0
  );
  // Profit loaded from the cache (store_profit_cache) on the initial table
  // load. Shown immediately on the collapsed month row and refreshed with the
  // live per-store breakdown once the month is expanded.
  const breakdownLoaded = Array.isArray(
    storeMonthlyBreakdownData?.[data.month_]?.goals
  );
  const useBreakdownProfit =
    lineExpended && !storeMonthlyBreakdownLoading && breakdownLoaded;

  const cachedProfitActual =
    data.profit_to_date !== "" && data.profit_to_date != null
      ? parseInt(data.profit_to_date) +
        (parseInt(data.actual_extra_profit) || 0)
      : null;
  const cachedProfitGoal = data.profit_goal ? parseInt(data.profit_goal) : null;

  const displayProfitActual = useBreakdownProfit
    ? sumProfit
    : cachedProfitActual;
  const displayProfitGoal = useBreakdownProfit
    ? sumProfitGoal
    : cachedProfitGoal;
  const hasProfit =
    displayProfitActual !== null &&
    displayProfitActual !== undefined &&
    !Number.isNaN(displayProfitActual);
  const totalProfitToDate = hasProfit
    ? `$${new Intl.NumberFormat().format(Math.floor(displayProfitActual))}   `
    : "";

  const totalProfitPrecent = displayProfitGoal
    ? Math.floor((displayProfitActual / displayProfitGoal) * 100)
    : 0;

  const getProfitPrecent = (data) => {
    return Math.floor(
      ((data.profit_to_date + data.actual_extra_profit - data.profit_goal) /
        Math.abs(data.profit_goal)) *
        100 +
        100
    );
  };

  return (
    <>
      <TableRowStyled
        style={{ padding: 0, borderWidth: 1, width: "100%" }}
        onClick={onClick}
        selected={selected === data.month ? true : false}
      >
        {innerTable ? (
          <InnerTableCellStyled>{data.store}</InnerTableCellStyled>
        ) : (
          <TableCellStyled>{data.month}</TableCellStyled>
        )}
        <TableCell style={innerTable ? { width: "3%" } : {}}>
          {data.days_left}
        </TableCell>
        <TableCell style={innerTable ? { width: "5%" } : {}}>
          {`$${new Intl.NumberFormat().format(Math.floor(data.sales_to_date))}`}
        </TableCell>
        <TableCell style={innerTable ? { width: "5.5%" } : {}}>
          {editableRow !== data.month ? (
            <GoalText>{`$${new Intl.NumberFormat().format(
              Math.floor(parseInt(data.goal))
            )}`}</GoalText>
          ) : null}
          {editableRow === data.month ? (
            <InputStyled
              defaultValue={new Intl.NumberFormat().format(
                Math.floor(parseInt(data.goal))
              )}
              value={activeRowGoal}
              onChange={onChangeGoal}
            />
          ) : null}
        </TableCell>
        <TableCell style={innerTable ? { width: "11.5%" } : {}}>
          <PredictedChart>
            <div>
              <Typography
                style={{
                  display: "inline-block",
                  width: "40px",
                  color: progressColor(false, data.actual_vs_goal),
                }}
              >
                {`${Math.round(data.actual_vs_goal)}%`}
              </Typography>
              <Typography
                style={{
                  display: "inline-block",
                  position: "relative",
                  marginLeft: showTodayPrecentage() ? -10 : 0,
                  fontSize: 10,
                  color: "gray",
                }}
              >
                {showTodayPrecentage() ? `of ${precentageGoalToday()}` : ""}
              </Typography>
            </div>
            <BorderLinearProgress
              variant="determinate"
              value={
                data.actual_vs_goal !== undefined
                  ? data.actual_vs_goal > 100
                    ? 100
                    : data.actual_vs_goal
                  : 0
              }
              style={{
                opacity: data.actual_vs_goal === 0 ? 0.5 : 1,
              }}
              goalColor={progressColor(true, data.actual_vs_goal)}
            />
          </PredictedChart>
        </TableCell>
        {data.store === "all" ? (
          <>
            <TableCell style={innerTable ? { width: "5%" } : {}}>
              {totalProfitToDate}
            </TableCell>
            <TableCell style={innerTable ? { width: "5%" } : {}}>
              {displayProfitGoal
                ? "$" + displayProfitGoal.toLocaleString()
                : ""}
            </TableCell>
            <TableCell style={innerTable ? { width: "10%" } : {}}>
              {hasProfit ? (
                <PredictedChart>
                  <div>
                    <Typography
                      style={{
                        display: "inline-block",
                        width: "50px",
                        color: progressColor(false, totalProfitPrecent),
                      }}
                    >
                      {`${Math.round(totalProfitPrecent)}%`}
                    </Typography>
                    <Typography
                      style={{
                        display: "inline-block",
                        position: "relative",
                        marginLeft:
                          showTodayPrecentage() || isYearlyTotal() ? -10 : 0,
                        fontSize: 10,
                        color: "gray",
                      }}
                    >
                      {isYearlyTotal()
                        ? `of ${yearlyPrecentageToday()}`
                        : showTodayPrecentage()
                        ? `of ${precentageGoalToday()}`
                        : ""}
                    </Typography>
                  </div>
                  <BorderLinearProgress
                    variant="determinate"
                    value={
                      totalProfitPrecent !== undefined
                        ? totalProfitPrecent > 100
                          ? 100
                          : totalProfitPrecent
                        : 0
                    }
                    style={{
                      opacity: totalProfitPrecent === 0 ? 0.5 : 1,
                    }}
                    goalColor={progressColor(true, totalProfitPrecent)}
                  />
                </PredictedChart>
              ) : (
                ""
              )}
            </TableCell>
            <TableCell align="right">
              <IconButton
                aria-label="expand row"
                size="small"
                onClick={() => {
                  onExpand(true);
                }}
              >
                <div
                  style={{
                    transform: lineExpended ? "rotate(90deg)" : null,
                  }}
                >
                  <img
                    src="/static/img/random/arrow-forward.svg"
                    alt="arrow-forward"
                  />
                </div>
              </IconButton>
            </TableCell>
          </>
        ) : (
          <>
            <TableCell style={innerTable ? { width: "4.5%" } : {}}>
              {data.profit_to_date && data.profit_to_date !== "" ? (
                <div style={{ display: "flex", alignItems: "center" }}>
                  {`$${new Intl.NumberFormat().format(
                    Math.floor(
                      parseInt(data.profit_to_date + data.actual_extra_profit)
                    )
                  )} `}
                  <Typography
                    component="span"
                    style={{
                      fontSize: "0.8em",
                      color: "gray",
                      fontStyle: "italic",
                      marginLeft: "4px",
                    }}
                  >
                    ({data.actual_extra_profit})
                  </Typography>
                </div>
              ) : null}
            </TableCell>
            <TableCell style={innerTable ? { width: "4.2%" } : {}}>
              {data.profit_goal && data.profit_goal !== ""
                ? `$${new Intl.NumberFormat().format(
                    Math.floor(parseInt(data.profit_goal))
                  )}`
                : null}
            </TableCell>
            <TableCell style={innerTable ? { width: "15%" } : {}}>
              <PredictedChart>
                <div>
                  <Typography
                    style={{
                      display: "inline-block",
                      width: "50px",
                      color: progressColor(false, getProfitPrecent(data)),
                    }}
                  >
                    {getProfitPrecent(data) + "%"}
                  </Typography>
                  <Typography
                    style={{
                      display: "inline-block",
                      position: "relative",
                      marginLeft:
                        showTodayPrecentage() || isYearlyTotal() ? -10 : 0,
                      fontSize: 10,
                      color: "gray",
                    }}
                  >
                    {isYearlyTotal()
                      ? `of ${yearlyPrecentageToday()}`
                      : showTodayPrecentage()
                      ? `of ${precentageGoalToday()}`
                      : ""}
                  </Typography>
                </div>
                <BorderLinearProgress
                  variant="determinate"
                  value={
                    getProfitPrecent(data) !== undefined
                      ? getProfitPrecent(data) > 100
                        ? 100
                        : getProfitPrecent(data)
                      : 0
                  }
                  style={{
                    opacity: getProfitPrecent(data) === 0 ? 0.5 : 1,
                  }}
                  goalColor={progressColor(true, getProfitPrecent(data))}
                />
              </PredictedChart>
            </TableCell>
          </>
        )}
      </TableRowStyled>
      <TableRowStyled>
        <TableCell
          align="right"
          style={{ padding: 0, borderBottom: 0 }}
          colSpan={10}
        >
          <Collapse
            in={lineExpended}
            timeout="auto"
            unmountOnExit
            style={{ padding: 0, borderBottom: 0 }}
          >
            <Box sx={{ margin: 1 }}>
              {storeMonthlyBreakdownLoading &&
              storeMonthlyBreakdownMonth === data.month_ ? (
                <>
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((item) => (
                    <Skeleton
                      key={item}
                      width={"95%"}
                      sx={{ mt: 5, ml: 5 }}
                      variant="rectangular"
                      height={15}
                    />
                  ))}
                </>
              ) : (
                <Table
                  size="small"
                  aria-label="purchases"
                  style={{ tableLayout: "fixed", width: "186%" }}
                >
                  <TableHead style={{ backgroundColor: "#F5F5F5" }}>
                    {breakdownGoals.map((item, index) => (
                      <GoalsTableRow
                        key={index}
                        data={item}
                        onClick={null}
                        onAddGoal={null}
                        activeRowGoal={null}
                        onChangeGoal={null}
                        selected={null}
                        editableRow={false}
                        breakdownData={null}
                      />
                    ))}
                  </TableHead>
                </Table>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRowStyled>
    </>
  );
};
export default GoalsTableRow;
